import { useEffect, useState } from "react";
import {
  Camera, BookOpenText, ShareNetwork, Printer, ForkKnife,
  LinkSimple, PencilSimple, Sparkle, UploadSimple, ClipboardText,
} from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useSiteFlags } from "@/contexts/SiteFlagsContext";
import AutoSeo from "@/components/AutoSeo";
import PageMeta from "@/components/PageMeta";

// Icons stay hardcoded — CMS only supplies text. Steps[0] gets the input-path
// sub-cards from `content.input_paths` (CMS-editable) with a hardcoded icon
// registry keyed by `icon_key` so admins can add / remove / reorder cards
// without shipping code.
const STEP_ICONS = [Sparkle, BookOpenText, ForkKnife, ShareNetwork, Printer];
const INPUT_PATH_ICONS = {
  camera: Camera,
  upload: UploadSimple,
  link: LinkSimple,
  paste: ClipboardText,
  pencil: PencilSimple,
  share: ShareNetwork,
};
const INPUT_PATH_FALLBACK = [
  { icon_key: "camera", label: "Scan a card", body: "Tap the camera card on /scan, point your phone at any handwritten recipe — even tea-stained and faded. Our AI maps every messy line into clean fields." },
  { icon_key: "upload", label: "PDF or Image upload", body: "Upload a file — including multi-page PDFs that merge into one recipe. Perfect for scanned cookbook pages or a recipe email you saved as PDF." },
  { icon_key: "link",   label: "Paste a link", body: "Found a recipe online you want to keep? Paste the URL — any cookery site, Instagram, TikTok or Pinterest. We grab the recipe and remember where it came from." },
  { icon_key: "paste",  label: "Paste text",   body: "Copy a recipe from an email, a Notes app entry or an old blog and let AI structure it into ingredients + steps." },
  { icon_key: "pencil", label: "Type one in",  body: "Already know it by heart? Skip the AI entirely and type. You'll get the full editor straight away — perfect for the recipes that live in your head." },
  { icon_key: "share",  label: "Share from social", body: "On Instagram, TikTok or Pinterest tap Share → Recipe Filer. One-tap saves land straight in your Inbox." },
];

const interp = (s, free) => (typeof s === "string" ? s.replaceAll("{FREE}", String(free)) : s);

export default function HowToUse() {
  const { limits } = useSiteFlags();
  const FREE = limits?.free_recipe_limit ?? 5;
  const [content, setContent] = useState(null);

  useEffect(() => {
    api.get("/public/site-content/how_to_use").then((r) => setContent(r.data.content)).catch(() => {});
  }, []);

  if (!content) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="howto-loading">
        <div className="w-8 h-8 border-2 border-rf-orange border-t-transparent rounded-full animate-spin" aria-hidden="true" />
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-6 md:px-10 py-20">
      <AutoSeo
        pageKey="how_to_use"
        fallbackTitle="How Recipe Filer works — scan, save, share, print"
        fallbackDescription="Guided tour of Recipe Filer: scan handwritten cards, import from URLs, add voice memories, cook with Kitchen View and order a real hardback cookbook."
      />
      <p className="overline mb-4">{content.overline}</p>
      <h1 className="font-display text-5xl md:text-7xl font-black text-rf-ink mb-6 leading-[0.95]">{content.h1}</h1>
      <p className="text-rf-ink-soft text-lg max-w-2xl mb-16 leading-relaxed">{content.intro}</p>

      <div className="space-y-12">
        {(content.steps || []).map((s, i) => {
          const Icon = STEP_ICONS[i] || Sparkle;
          return (
            <div key={i} className="grid md:grid-cols-12 gap-6 items-start">
              <div className="md:col-span-3">
                <div className="w-16 h-16 rounded-2xl bg-rf-amber flex items-center justify-center">
                  <Icon size={32} weight="duotone" className="text-rf-ink" />
                </div>
              </div>
              <div className="md:col-span-9">
                <h2 className="font-display text-2xl md:text-4xl font-black text-rf-ink mb-3 leading-tight">{s.title}</h2>
                <p className="text-lg text-rf-ink-soft leading-relaxed">{s.body}</p>

                {/* First step gets the 3 input-path sub-cards + iOS share CTA */}
                {i === 0 && (
                  <>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6" data-testid="howto-input-paths">
                      {(content.input_paths || INPUT_PATH_FALLBACK).map((p, pi) => {
                        const PathIcon = INPUT_PATH_ICONS[p.icon_key] || Camera;
                        return (
                          <div key={`${p.icon_key}-${pi}`} className="card-recipe">
                            <div className="w-12 h-12 rounded-xl bg-rf-amber/20 flex items-center justify-center mb-3">
                              <PathIcon size={24} weight="duotone" className="text-rf-orange" />
                            </div>
                            <p className="font-display font-extrabold text-rf-ink mb-2">{p.label}</p>
                            <p className="text-sm text-rf-ink-soft leading-relaxed">{p.body}</p>
                          </div>
                        );
                      })}
                    </div>
                    <div className="mt-4 p-5 rounded-2xl bg-rf-amber/15 border border-rf-amber/40 flex flex-wrap items-center gap-4" data-testid="howto-ios-cta">
                      <span className="shrink-0 text-xs font-black uppercase tracking-[0.18em] text-rf-ink bg-rf-amber rounded-full px-3 py-1.5">iOS iPhone</span>
                      <div className="flex-1 min-w-[200px]">
                        <p className="font-display font-extrabold text-rf-ink leading-tight">{content.ios_card_title}</p>
                        <p className="text-sm text-rf-ink-soft mt-0.5">{content.ios_card_body}</p>
                      </div>
                      <Link to="/ios-share-setup" data-testid="howto-ios-cta-btn" className="btn-amber !py-2.5 !px-5 text-sm whitespace-nowrap inline-flex items-center gap-1.5">
                        {content.ios_cta_label}
                      </Link>
                    </div>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-20 p-10 rounded-3xl bg-rf-ink text-rf-cream text-center">
        <h2 className="font-display text-3xl md:text-5xl font-black mb-4">{content.cta_h2}</h2>
        <p className="text-rf-cream/70 max-w-xl mx-auto mb-6">{interp(content.cta_body, FREE)}</p>
        <Link to="/register" data-testid="howto-cta-start" className="btn-amber mt-4 inline-flex">{content.cta_button_label}</Link>
      </div>
    </div>
  );
}
